import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, serverTimestamp, updateDoc, type DocumentData } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { X } from 'lucide-react';
import SignaturePad from '../../engagement/SignaturePad';
import { auth, db, storage } from '../../../utils/firebase';
import { getPublicSettings } from '../../../utils/appSettings';
import type { JobApplication, JobOfferClauses, JobOfferFields, JobOfferLetter, JobPosting } from '../../../types/hiring';
import { JOB_EMPLOYMENT_TYPES, JOB_EMPLOYMENT_TYPE_LABELS } from '../../../types/hiring';
import type { PayrollPosition, PayrollSettings } from '../../../types/finances';
import { useCRM } from '../../../utils/CRMContext';
import { crmUsersToOrgPeople } from '../../../utils/orgChart';
import { managerCandidates } from '../../../utils/orgReporting';
import {
  assembleEmploymentAgreementSections,
  assembleJobOfferSections,
  defaultOfferClauses,
  defaultOfferFields,
  employeeFullName,
  employmentAgreementIsReleased,
  offerLetterIsSigned,
  offerReportsToPosition,
  publicOfferUrl,
} from '../../../utils/jobOfferLetter';
import { offerDefaultsFromHiring, payrollPatchFromOffer } from '../../../utils/jobOfferPayroll';
import {
  createJobOfferDraft,
  saveJobOfferDraft,
  sendJobOfferContract,
  sendJobOfferDraft,
  voidJobOffer,
} from '../../../utils/jobOfferLettersFirestore';
import { mapPayrollPositionFromFirestore, mapPayrollSettingsFromFirestore } from '../../../utils/payrollFirestore';
import { subscribePublishedHandbookPolicies } from '../../../utils/handbookPoliciesFirestore';
import type { HandbookPolicy } from '../../../types/handbookPolicy';
import JobOfferPreview from './JobOfferPreview';
import PublicJobOfferView, { LCTF_WORDMARK_SRC, type OfferPortalNavStep, type OfferPortalPolicy } from './PublicJobOfferView';

type Props = {
  application: JobApplication;
  posting?: JobPosting | null;
  offer: JobOfferLetter | null;
  composeOpen: boolean;
  onRequestCompose: () => void;
};

const JobOfferPane: React.FC<Props> = ({ application, posting, offer, composeOpen, onRequestCompose }) => {
  const { crmUsers } = useCRM();
  const reportsToPeople = useMemo(
    () => managerCandidates(crmUsersToOrgPeople(crmUsers)),
    [crmUsers],
  );
  const [fields, setFields] = useState<JobOfferFields>(() => defaultOfferFields(application));
  const [clauses, setClauses] = useState<JobOfferClauses>(() =>
    defaultOfferClauses(application.jobTitle, 'salary'),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ceoDraft, setCeoDraft] = useState<{ dataUrl: string; signedBy: string } | null>(null);
  const [position, setPosition] = useState<PayrollPosition | null>(null);
  const [payrollSettings, setPayrollSettings] = useState<PayrollSettings | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [docTab, setDocTab] = useState<'offer' | 'contract'>('offer');
  const [previewStep, setPreviewStep] = useState<OfferPortalNavStep>('offer');
  const [previewPolicies, setPreviewPolicies] = useState<OfferPortalPolicy[]>([]);
  const [logoUrl, setLogoUrl] = useState(LCTF_WORDMARK_SRC);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const appliedPayrollRef = useRef(false);
  const draftIdRef = useRef<string | null>(null);
  const hydratedOfferIdRef = useRef<string | null>(null);
  const skipAutosaveRef = useRef(true);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    appliedPayrollRef.current = false;
    draftIdRef.current = null;
    hydratedOfferIdRef.current = null;
    skipAutosaveRef.current = true;
    setPosition(null);
  }, [application.id]);

  useEffect(() => {
    return subscribePublishedHandbookPolicies(
      (list: HandbookPolicy[]) => {
        setPreviewPolicies(list.map(p => ({
          id: p.id,
          title: p.title,
          policyNumber: p.policyNumber,
          summary: p.summary,
          body: p.body,
          bodyFormat: p.bodyFormat,
        })));
      },
      () => setPreviewPolicies([]),
      { userId: auth.currentUser?.uid || '' },
    );
  }, []);

  const openPreview = (step: OfferPortalNavStep = docTab === 'contract' ? 'contract' : 'offer') => {
    setPreviewStep(step);
    if (step === 'offer' || step === 'contract') setDocTab(step);
    setPreviewOpen(true);
  };

  const handlePreviewNav = (next: OfferPortalNavStep) => {
    setPreviewStep(next);
    if (next === 'offer' || next === 'contract') setDocTab(next);
  };

  useEffect(() => {
    let cancelled = false;
    void getPublicSettings().then(settings => {
      if (cancelled) return;
      setLogoUrl(settings?.engagementLogo?.url || LCTF_WORDMARK_SRC);
      setBackgroundUrl(settings?.engagementBackground?.url || null);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = application.payrollPositionId || posting?.payrollPositionId || '';
    if (!id) {
      setPosition(null);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [posSnap, settingsSnap] = await Promise.all([
          getDoc(doc(db, 'payrollPositions', id)),
          getDoc(doc(db, 'financeSettings', 'payrollSettings')),
        ]);
        if (cancelled) return;
        setPosition(posSnap.exists()
          ? mapPayrollPositionFromFirestore(posSnap.id, posSnap.data() as Record<string, unknown>)
          : null);
        setPayrollSettings(mapPayrollSettingsFromFirestore(settingsSnap.data() as Record<string, unknown> | undefined));
      } catch {
        if (!cancelled) setPosition(null);
      }
    })();
    return () => { cancelled = true; };
  }, [application.payrollPositionId, posting?.payrollPositionId]);

  useEffect(() => {
    if (offer) {
      if (offer.status === 'draft') draftIdRef.current = offer.id;
      if (hydratedOfferIdRef.current !== offer.id) {
        hydratedOfferIdRef.current = offer.id;
        skipAutosaveRef.current = true;
        setFields(offer.fields);
        setClauses(offer.clauses);
      }
      return;
    }
    if (appliedPayrollRef.current) return;
    if (!position && !posting) return;
    appliedPayrollRef.current = true;
    skipAutosaveRef.current = true;
    const next = offerDefaultsFromHiring({
      application,
      posting,
      position,
      settings: payrollSettings,
    });
    setFields(next.fields);
    setClauses(next.clauses);
  }, [offer, position, posting, payrollSettings, application]);

  const sections = useMemo(() => assembleJobOfferSections(fields, clauses), [fields, clauses]);
  const agreementSections = useMemo(() => assembleEmploymentAgreementSections(fields, clauses), [fields, clauses]);
  const ceoSigForPreview = ceoDraft
    ? { dataUrl: ceoDraft.dataUrl, signedBy: ceoDraft.signedBy, signedAt: new Date(), title: fields.ceoTitle }
    : offer?.ceoSignature;
  const locked = offer?.status === 'signed';
  const sent = offer?.status === 'sent';
  const showForm = composeOpen || offer?.status === 'draft' || (!offer && composeOpen);

  const patchField = <K extends keyof JobOfferFields>(key: K, value: JobOfferFields[K]) => {
    setFields(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'payType') {
        setClauses(c => ({
          ...c,
          hourlyOvertime: value === 'hourly',
          salaryNoOvertime: value === 'salary' ? c.salaryNoOvertime || c.travelInstall : false,
        }));
      }
      return next;
    });
  };

  const ceoSignature = () => {
    if (ceoDraft) {
      return {
        dataUrl: ceoDraft.dataUrl,
        signedBy: ceoDraft.signedBy,
        signedAt: new Date(),
        title: fields.ceoTitle,
      };
    }
    return offer?.ceoSignature || null;
  };

  const persistDraft = async () => {
    const existingId = draftIdRef.current || (offer?.status === 'draft' ? offer.id : null);
    if (existingId) {
      await saveJobOfferDraft(existingId, { fields, clauses, ceoSignature: ceoSignature() });
      draftIdRef.current = existingId;
      hydratedOfferIdRef.current = existingId;
      return existingId;
    }
    const id = await createJobOfferDraft({ applicationId: application.id, fields, clauses });
    draftIdRef.current = id;
    hydratedOfferIdRef.current = id;
    const sig = ceoSignature();
    if (sig) await saveJobOfferDraft(id, { fields, clauses, ceoSignature: sig });
    return id;
  };

  const syncPlannedPayroll = async () => {
    const id = position?.id || application.payrollPositionId || posting?.payrollPositionId;
    if (!id || (position && position.status !== 'planned' && position.status !== 'open')) return;
    try {
      const patch = payrollPatchFromOffer(fields, clauses);
      const payload: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp() };
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) delete payload[key];
      });
      await updateDoc(doc(db, 'payrollPositions', id), payload as DocumentData);
    } catch {
      /* hiring can still send if payroll write is blocked */
    }
  };

  useEffect(() => {
    if (!showForm) return undefined;
    if (offer && offer.status !== 'draft') return undefined;
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return undefined;
    }
    setSaveState('saving');
    const timer = window.setTimeout(() => {
      void persistDraft()
        .then(() => syncPlannedPayroll())
        .then(() => setSaveState('saved'))
        .catch(e => {
          setSaveState('idle');
          setError((e as Error).message);
        });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [fields, clauses, ceoDraft, showForm, offer?.status]);

  const handleSend = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!fields.startDate || !fields.payAmount) throw new Error('Start date and pay amount are required');
      if (!ceoSignature()) throw new Error('CEO must sign first');
      const id = await persistDraft();
      await syncPlannedPayroll();
      await sendJobOfferDraft(id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleSendContract = async () => {
    if (!offer) return;
    setBusy(true);
    setError(null);
    try {
      if (!ceoSignature() && !offer.agreementCeoSignature && !offer.ceoSignature) {
        throw new Error('CEO must sign before sending the employment agreement');
      }
      await sendJobOfferContract(offer.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleVoid = async () => {
    if (!offer || offer.status !== 'sent') return;
    if (!window.confirm('Void this offer? The candidate link will stop working.')) return;
    setBusy(true);
    try {
      await voidJobOffer(offer.id);
      onRequestCompose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const downloadSignedPdf = async () => {
    if (!offer) return;
    setBusy(true);
    setError(null);
    try {
      const url = offer.signedPdfUrl || await getDownloadURL(ref(storage, `job-offer-letters/${offer.id}/signed.pdf`));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError((e as Error).message || 'Signed PDF is not ready yet');
    } finally {
      setBusy(false);
    }
  };

  if (!offer && !composeOpen) {
    return (
      <div id="hiring-section-offer" className="people-hiring-offer-cta">
        <button type="button" className="ops-new-btn" onClick={onRequestCompose}>
          Extend offer
        </button>
      </div>
    );
  }

  if (offer && (sent || locked)) {
    return (
      <div id="hiring-section-offer" className="people-hiring-offer-sent">
        <div className="people-hiring-section-head">
          <h3>{locked ? 'Offer and employment agreement' : employmentAgreementIsReleased(offer) ? 'Offer and contract sent' : 'Offer letter sent'}</h3>
          <div className="people-hiring-offer-toolbar">
            {locked && (
              <button type="button" className="pm-btn pm-btn-secondary" onClick={() => void downloadSignedPdf()} disabled={busy}>
                Download signed PDF
              </button>
            )}
            {sent && !employmentAgreementIsReleased(offer) && (
              <button type="button" className="ops-new-btn" onClick={() => void handleSendContract()} disabled={busy}>
                {busy ? 'Sending…' : 'Send employment agreement'}
              </button>
            )}
            <button type="button" className="pm-btn pm-btn-secondary" onClick={() => openPreview()}>
              Preview public page
            </button>
            {sent && (
              <button type="button" className="pm-btn pm-btn-ghost" onClick={() => void handleVoid()} disabled={busy}>
                Void & reissue
              </button>
            )}
          </div>
        </div>
        <p className="people-hiring-muted">
          Portal: <a href={publicOfferUrl(offer.slug)} target="_blank" rel="noreferrer">{publicOfferUrl(offer.slug)}</a>
          {offer.sentAt ? ` · Letter sent ${offer.sentAt.toLocaleDateString()}` : ''}
          {offer.contractSentAt ? ` · Contract sent ${offer.contractSentAt.toLocaleDateString()}` : sent ? ' · Contract not sent yet' : ''}
          {offer.signedAt ? ` · Both signed ${offer.signedAt.toLocaleDateString()}` : ''}
        </p>
        {error && <p className="people-hiring-resume-error">{error}</p>}
        <div className="people-hiring-doc-tabs" role="tablist" aria-label="Offer documents">
          <button type="button" className={docTab === 'offer' ? 'is-current' : ''} onClick={() => setDocTab('offer')}>
            Offer letter
          </button>
          <button type="button" className={docTab === 'contract' ? 'is-current' : ''} onClick={() => setDocTab('contract')}>
            Employment agreement
          </button>
        </div>
        {docTab === 'offer' ? (
          <JobOfferPreview
            sections={offer.sections}
            employeeName={employeeFullName(offer.fields)}
            ceoName={offer.fields.ceoName}
            ceoTitle={offer.fields.ceoTitle}
            ceoSignature={offer.ceoSignature}
            employeeSignature={offer.employeeSignature}
            documentTitle="Offer Letter"
          />
        ) : (
          <JobOfferPreview
            sections={offer.agreementSections.length ? offer.agreementSections : agreementSections}
            employeeName={employeeFullName(offer.fields)}
            ceoName={offer.fields.ceoName}
            ceoTitle={offer.fields.ceoTitle}
            ceoSignature={offer.agreementCeoSignature || offer.ceoSignature}
            employeeSignature={offer.agreementEmployeeSignature}
            documentTitle="Employment Agreement"
            companySignName="LC TIMBER FRAMES"
          />
        )}
        {previewOpen && (
          <div className="people-hiring-modal-backdrop people-hiring-offer-preview-backdrop" onClick={() => setPreviewOpen(false)}>
            <div className="people-hiring-modal people-hiring-offer-preview-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Public offer preview">
              <div className="people-hiring-modal-header">
                <h3>Candidate page preview</h3>
                <button type="button" className="pm-btn pm-btn-ghost" onClick={() => setPreviewOpen(false)} aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <div className="people-hiring-offer-preview-body">
                <PublicJobOfferView
                  fields={offer.fields}
                  sections={previewStep === 'contract'
                    ? (offer.agreementSections.length ? offer.agreementSections : agreementSections)
                    : offer.sections}
                  ceoSignature={previewStep === 'contract' ? (offer.agreementCeoSignature || offer.ceoSignature) : offer.ceoSignature}
                  employeeSignature={previewStep === 'contract' ? offer.agreementEmployeeSignature : offer.employeeSignature}
                  logoUrl={logoUrl}
                  backgroundUrl={backgroundUrl}
                  preview
                  documentKind={previewStep === 'contract' ? 'contract' : 'offer'}
                  navStep={previewStep}
                  onNavStep={handlePreviewNav}
                  offerSigned={offerLetterIsSigned(offer)}
                  contractSigned={Boolean(offer.agreementEmployeeSignature) || offer.status === 'signed'}
                  contractReleased={employmentAgreementIsReleased(offer)}
                  policies={previewPolicies}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!showForm) return null;

  return (
    <div id="hiring-section-offer" className="people-hiring-offer-compose">
      <div className="people-hiring-section-head">
        <h3>Extend offer</h3>
        <button type="button" className="pm-btn pm-btn-secondary" onClick={() => openPreview()}>
          Preview public page
        </button>
      </div>
      <p className="people-hiring-muted" style={{ margin: '0 0 10px' }}>
        {position
          ? `Synced from planned payroll: ${position.title}${position.status ? ` (${position.status})` : ''}.`
          : posting?.payrollPositionId
            ? 'Linked payroll position could not be loaded. Using the job posting.'
            : 'No planned payroll position is linked to this posting.'}
      </p>
      <div className="people-hiring-offer-grid">
        <div className="people-hiring-offer-fields">
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>Position</label>
              <input value={fields.positionTitle} onChange={e => patchField('positionTitle', e.target.value)} />
            </div>
            <div className="people-hiring-field">
              <label>Start date</label>
              <input type="date" value={fields.startDate} onChange={e => patchField('startDate', e.target.value)} />
            </div>
          </div>
          <div className="people-hiring-field">
            <label>Duties</label>
            <textarea value={fields.duties} rows={4} onChange={e => patchField('duties', e.target.value)} />
          </div>
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>Pay type</label>
              <select value={fields.payType} onChange={e => patchField('payType', e.target.value as JobOfferFields['payType'])}>
                <option value="salary">Salary</option>
                <option value="hourly">Hourly</option>
              </select>
            </div>
            <div className="people-hiring-field">
              <label>{fields.payType === 'hourly' ? 'Hourly rate' : 'Annual salary'}</label>
              <input
                type="number"
                min={0}
                value={fields.payAmount || ''}
                onChange={e => patchField('payAmount', Number(e.target.value) || 0)}
              />
            </div>
          </div>
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>Hours / week</label>
              <input
                type="number"
                min={1}
                value={fields.hoursPerWeek}
                onChange={e => patchField('hoursPerWeek', Number(e.target.value) || 40)}
              />
            </div>
            <div className="people-hiring-field">
              <label>Employment type</label>
              <select value={fields.employmentType} onChange={e => patchField('employmentType', e.target.value as JobOfferFields['employmentType'])}>
                {JOB_EMPLOYMENT_TYPES.map(t => (
                  <option key={t} value={t}>{JOB_EMPLOYMENT_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="people-hiring-field">
            <label>Location</label>
            <input value={fields.location} onChange={e => patchField('location', e.target.value)} />
          </div>
          <div className="people-hiring-row">
            <div className="people-hiring-field">
              <label>Reports to</label>
              <select
                value={
                  fields.reportingToUserId
                  || reportsToPeople.find(p => offerReportsToPosition(p) === fields.reportingTo)?.uid
                  || ''
                }
                onChange={e => {
                  const person = reportsToPeople.find(p => p.uid === e.target.value);
                  setFields(prev => ({
                    ...prev,
                    reportingToUserId: person?.uid || '',
                    reportingTo: person ? offerReportsToPosition(person) : '',
                  }));
                }}
              >
                <option value="">Select a person</option>
                {fields.reportingTo && !reportsToPeople.some(p =>
                  p.uid === fields.reportingToUserId || offerReportsToPosition(p) === fields.reportingTo,
                ) && (
                  <option value={fields.reportingToUserId || fields.reportingTo}>{fields.reportingTo}</option>
                )}
                {reportsToPeople.map(p => (
                  <option key={p.uid} value={p.uid}>
                    {p.title ? `${p.displayName} — ${p.title}` : p.displayName}
                  </option>
                ))}
              </select>
              {fields.reportingTo ? (
                <p className="people-hiring-muted" style={{ margin: '6px 0 0' }}>
                  Offer lists the {fields.reportingTo} role, not the person.
                </p>
              ) : (
                <p className="people-hiring-muted" style={{ margin: '6px 0 0' }}>
                  Pick the supervisor. The offer letter names their position.
                </p>
              )}
            </div>
            <div className="people-hiring-field">
              <label>Respond by</label>
              <input type="date" value={fields.responseDeadline} onChange={e => patchField('responseDeadline', e.target.value)} />
            </div>
          </div>

          <fieldset className="people-hiring-clauses">
            <legend>Additional clauses</legend>
            <label>
              <input type="checkbox" checked={clauses.travelInstall} onChange={e => setClauses(c => ({ ...c, travelInstall: e.target.checked }))} />
              Travel and installation assignments
            </label>
            <label>
              <input type="checkbox" checked={clauses.salaryNoOvertime} disabled={fields.payType !== 'salary'} onChange={e => setClauses(c => ({ ...c, salaryNoOvertime: e.target.checked }))} />
              Salaried — no overtime / 12-hour install days
            </label>
            <label>
              <input type="checkbox" checked={clauses.hourlyOvertime} disabled={fields.payType !== 'hourly'} onChange={e => setClauses(c => ({ ...c, hourlyOvertime: e.target.checked }))} />
              Hourly — overtime eligible
            </label>
            <label>
              <input type="checkbox" checked={clauses.executiveResponsibilities} onChange={e => setClauses(c => ({ ...c, executiveResponsibilities: e.target.checked }))} />
              Executive responsibilities block
            </label>
            <label>
              <input
                type="checkbox"
                checked={clauses.salesCommission}
                onChange={e => setClauses(c => ({
                  ...c,
                  salesCommission: e.target.checked,
                  salesCommissionPercent: e.target.checked ? (c.salesCommissionPercent ?? 1) : c.salesCommissionPercent,
                }))}
              />
              Sales commission — percent of the purchase price per timber frame sold
            </label>
            {clauses.executiveResponsibilities && (
              <textarea
                rows={5}
                value={clauses.executiveResponsibilitiesText}
                onChange={e => setClauses(c => ({ ...c, executiveResponsibilitiesText: e.target.value }))}
              />
            )}
            <div className="people-hiring-row">
              {clauses.salesCommission && (
                <div className="people-hiring-field">
                  <label>Sales commission %</label>
                  <input
                    type="number"
                    min={0}
                    step="0.1"
                    value={clauses.salesCommissionPercent ?? 1}
                    onChange={e => setClauses(c => ({
                      ...c,
                      salesCommissionPercent: e.target.value === '' ? null : Number(e.target.value),
                    }))}
                  />
                </div>
              )}
              <div className="people-hiring-field">
                <label>Profit share %</label>
                <input
                  type="number"
                  min={0}
                  value={clauses.profitSharePercent ?? ''}
                  onChange={e => setClauses(c => ({ ...c, profitSharePercent: e.target.value === '' ? null : Number(e.target.value) }))}
                />
              </div>
              <div className="people-hiring-field">
                <label>SEP IRA %</label>
                <input
                  type="number"
                  min={0}
                  value={clauses.sepIraPercent ?? ''}
                  onChange={e => setClauses(c => ({ ...c, sepIraPercent: e.target.value === '' ? null : Number(e.target.value) }))}
                />
              </div>
            </div>
            <div className="people-hiring-field">
              <label>Monthly health reimbursement</label>
              <input
                type="number"
                min={0}
                value={clauses.healthReimbursementMonthly ?? ''}
                onChange={e => setClauses(c => ({ ...c, healthReimbursementMonthly: e.target.value === '' ? null : Number(e.target.value) }))}
              />
            </div>
          </fieldset>

          <div className="people-hiring-field">
            <label>CEO signs first</label>
            <p className="people-hiring-muted" style={{ margin: '0 0 8px' }}>
              Your signature is applied to both the offer letter and the employment agreement. The candidate signs each document as a separate step.
            </p>
            <SignaturePad
              initialName={fields.ceoName}
              onChange={setCeoDraft}
              onSign={setCeoDraft}
              hideSubmitButton
            />
          </div>

          {error && <p className="people-hiring-resume-error">{error}</p>}
          <p className="people-hiring-muted" style={{ margin: '8px 0 0' }}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : 'Changes save automatically.'}
          </p>
          <div className="people-hiring-modal-footer">
            <button type="button" className="pm-btn pm-btn-secondary" onClick={() => openPreview()}>
              Preview public page
            </button>
            <button type="button" className="ops-new-btn" disabled={busy || (!ceoDraft && !offer?.ceoSignature)} onClick={() => void handleSend()}>
              {busy ? 'Sending…' : 'Sign & send offer letter'}
            </button>
          </div>
        </div>
        <div>
          <div className="people-hiring-doc-tabs" role="tablist" aria-label="Offer documents">
            <button type="button" className={docTab === 'offer' ? 'is-current' : ''} onClick={() => setDocTab('offer')}>
              Offer letter
            </button>
            <button type="button" className={docTab === 'contract' ? 'is-current' : ''} onClick={() => setDocTab('contract')}>
              Employment agreement
            </button>
          </div>
          {docTab === 'offer' ? (
            <JobOfferPreview
              sections={sections}
              employeeName={employeeFullName(fields)}
              ceoName={fields.ceoName}
              ceoTitle={fields.ceoTitle}
              ceoSignature={ceoSigForPreview}
              documentTitle="Offer Letter"
            />
          ) : (
            <JobOfferPreview
              sections={agreementSections}
              employeeName={employeeFullName(fields)}
              ceoName={fields.ceoName}
              ceoTitle={fields.ceoTitle}
              ceoSignature={ceoSigForPreview}
              documentTitle="Employment Agreement"
              companySignName="LC TIMBER FRAMES"
            />
          )}
        </div>
      </div>
      {previewOpen && (
        <div className="people-hiring-modal-backdrop people-hiring-offer-preview-backdrop" onClick={() => setPreviewOpen(false)}>
          <div className="people-hiring-modal people-hiring-offer-preview-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Public offer preview">
            <div className="people-hiring-modal-header">
              <h3>Candidate page preview</h3>
              <button type="button" className="pm-btn pm-btn-ghost" onClick={() => setPreviewOpen(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="people-hiring-offer-preview-body">
              <PublicJobOfferView
                fields={fields}
                sections={previewStep === 'contract' ? agreementSections : sections}
                ceoSignature={ceoSigForPreview}
                employeeSignature={offer?.employeeSignature}
                logoUrl={logoUrl}
                backgroundUrl={backgroundUrl}
                preview
                documentKind={previewStep === 'contract' ? 'contract' : 'offer'}
                navStep={previewStep}
                onNavStep={handlePreviewNav}
                offerSigned={false}
                contractReleased
                policies={previewPolicies}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobOfferPane;
